import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { logError } from "@entrilia/shared/logging";
import { sanitizeEmail } from "@entrilia/shared/utilities/emailHelper";
import { emailValidator } from "@entrilia/shared/utilities/validators";
import type { GetInvestorPortalUserStatusResponse } from "../../../api/clientsManagementTypes";
import { api } from "../../../api/operationsApi";
import useDebounce from "@entrilia/shared/hooks/useDebounce";
import { useExecutionState } from "../../../hooks/useExecutionState";
import ExecutionStatusAlert from "../../common/ExecutionStatusAlert";
import FieldHelp from "../../common/FieldHelp";
import PageTitle from "../../common/PageTitle";

const getInvestorPortalUserStatus = withErrorHandling(api.usersManagement.getInvestorPortalUserStatus);
const resetInvestorPortalUserPassword = withErrorHandling(api.usersManagement.resetInvestorPortalUserPassword);

type InvestorPortalLookupState =
  | { status: "idle" }
  | { status: "in-progress" }
  | { status: "success"; data: GetInvestorPortalUserStatusResponse }
  | { status: "error"; errorMessage: string };

const ResetInvestorPortalPasswordTab = () => {
  const { executionState, setExecutionState } = useExecutionState();
  const [userEmail, setUserEmail] = useState("");
  const [lookupState, setLookupState] = useState<InvestorPortalLookupState>({ status: "idle" });

  const debouncedLookup = useDebounce(async (email: string) => {
    setLookupState({ status: "in-progress" });

    const [resp, error] = await getInvestorPortalUserStatus({ email });

    if (error) {
      logError(error, "[ResetInvestorPortalPasswordTab] getInvestorPortalUserStatus");
      setLookupState({
        status: "error",
        errorMessage: error.message,
      });
      return;
    }

    setLookupState({
      status: "success",
      data: resp,
    });
  }, 500);

  const handleResetPassword = async () => {
    const sanitizedEmail = sanitizeEmail(userEmail);
    if (!sanitizedEmail) {
      return;
    }

    if (lookupState.status !== "success" || !lookupState.data.canResetPassword) {
      return;
    }

    setExecutionState({ status: "in-progress" });
    const [resp, error] = await resetInvestorPortalUserPassword({ email: sanitizedEmail });

    if (error) {
      logError(error, "[ResetInvestorPortalPasswordTab] resetInvestorPortalUserPassword");
      setExecutionState({ status: "error", error });
      return;
    }

    setExecutionState({
      status: "success",
      text: "Password reset successful",
      details: [`Email: ${resp.email}`, `Temporary password: ${resp.tempPassword}`],
    });
  };

  const handleUserEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setUserEmail(newValue);
    setExecutionState({ status: "idle" });
    setLookupState({ status: "idle" });

    const sanitizedEmail = sanitizeEmail(newValue);
    const validation = sanitizedEmail ? emailValidator(sanitizedEmail) : undefined;
    if (sanitizedEmail && validation?.isValid) {
      debouncedLookup(sanitizedEmail);
    }
  };

  const canResetInvestorPortalPassword = lookupState.status === "success" && lookupState.data.canResetPassword;

  const sanitizedEmail = sanitizeEmail(userEmail);
  const emailValidationResult = sanitizedEmail ? emailValidator(sanitizedEmail) : undefined;

  return (
    <Stack spacing={3} sx={{ alignItems: "flex-start", width: "24rem" }}>
      <PageTitle title="Reset Investor Portal Password" subtitle="🔐 About this operation" defaultExpanded>
        Reset an Investor Portal user password using email only.
        <br />
        <br />
        As you type a valid email, the page checks:
        <br />
        • whether the user exists in Azure B2C
        <br />
        • which client Investor Portals they can access
        <br />
        <br />
        You can reset the password only when the user is eligible.
      </PageTitle>
      <Stack direction="row" sx={{ alignItems: "center", width: "100%" }}>
        <TextField
          fullWidth
          label="User Email"
          value={userEmail}
          onChange={handleUserEmailChange}
          error={emailValidationResult?.isValid === false}
          helperText={emailValidationResult?.error}
        />
        <FieldHelp>Email address of the Investor Portal user whose password needs to be reset.</FieldHelp>
      </Stack>
      {lookupState.status === "in-progress" && (
        <Typography variant="body2" color="textSecondary">
          Checking user status...
        </Typography>
      )}
      {lookupState.status === "error" && (
        <Alert severity="error" sx={{ width: "100%" }}>
          Failed to load user status. {lookupState.errorMessage}
        </Alert>
      )}
      {lookupState.status === "success" && (
        <Alert severity={lookupState.data.canResetPassword ? "success" : "warning"} sx={{ width: "100%" }}>
          <Stack spacing={1}>
            <Typography variant="body2">
              User exists in Azure B2C: <strong>{lookupState.data.userExists ? "Yes" : "No"}</strong>
            </Typography>
            <Typography variant="body2">
              Can reset password: <strong>{lookupState.data.canResetPassword ? "Yes" : "No"}</strong>
            </Typography>
            <Typography variant="body2">Investor Portal clients:</Typography>
            {lookupState.data.clients.length === 0 && (
              <Typography variant="body2" color="textSecondary">
                No Investor Portal access found.
              </Typography>
            )}
            {lookupState.data.clients.map((client) => (
              <Typography key={client.clientCode} variant="body2">
                • {client.clientTitle} ({client.clientCode})
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}
      <Button
        variant="contained"
        color="primary"
        onClick={handleResetPassword}
        loading={executionState.status === "in-progress"}
        disabled={!userEmail.trim() || !canResetInvestorPortalPassword}
      >
        Reset Password
      </Button>
      <ExecutionStatusAlert executionState={executionState} setExecutionState={setExecutionState} />
    </Stack>
  );
};

export default ResetInvestorPortalPasswordTab;
